/**
 * Refresh token flow with a module-level mutex.
 *
 * When several requests fire 401 in parallel (a common pattern after the
 * access token expires), they all need a fresh token but only one renewal
 * should actually run. We dedupe by sharing a single in-flight
 * `Promise<boolean>` across all concurrent callers.
 *
 * The renewal itself is pluggable: `ensureKeycloakLogin()` plugs
 * `keycloak.updateToken(-1)` via `setRefreshHandler`. Without a handler set
 * (the pre-init window), `tryRefresh()` resolves `false` — the password-grant
 * transport (`POST /public/auth/refresh`) died in the 3-app carve (C5).
 *
 * ponytail: mutex + seam kept; the password-grant doRefresh is what died.
 * The three fetchers (shared client/fetcher, kds api) and keycloak all rely on
 * the "never throws → boolean, deduped" contract — arrancar o seam obrigaria a
 * reescrever os 3 call-sites por zero ganho.
 *
 * See DESIGN section 8.2.
 */

let refreshPromise: Promise<boolean> | null = null;

export type RefreshHandler = () => Promise<boolean>;

/**
 * Pluggable refresh handler. When the tab's session is OIDC (keycloak-js), the
 * renewal is `keycloak.updateToken` — set by `ensureKeycloakLogin()`. Default
 * `null` = no renewal available → `tryRefresh` resolves `false` (session dead,
 * caller bounces to login).
 */
let refreshHandler: RefreshHandler | null = null;

export const setRefreshHandler = (handler: RefreshHandler | null): void => {
  refreshHandler = handler;
};

/**
 * Attempts to refresh the access token. Concurrent callers all observe the
 * same in-flight promise; the actual renewal runs at most once per cycle.
 *
 * Returns `true` on success, `false` on any failure (no handler plugged,
 * handler rejects/resolves false). Never throws — the caller is expected to
 * act on the boolean (typically: `forceLogin()` on `false`).
 */
export const tryRefresh = async (): Promise<boolean> => {
  if (refreshPromise) return refreshPromise;
  const run = refreshHandler ?? (async (): Promise<boolean> => false);
  refreshPromise = run()
    .catch(() => false) // tryRefresh contract: never throws
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
};
